import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";

import { IClassWithStudents, IActiveLearningAnswers } from "../../../../state";
import Button from "../../../../atoms/ActiveLearning/Button";
import { IAnswer } from "../../../../constants/active_learning";
import styles from "../ActiveLearningIssue.module.scss";
import Selector from "../../../../atoms/ActiveLearning/Selector";
import TextArea from "../../../../atoms/ActiveLearning/TextArea";
import RadioButton from "../../../../atoms/ActiveLearning/RadioButton";
import ActiveLearningModalConfirm from "../../../../molecules/ActiveLearningModalConfirm";

interface ICurrentAnswer extends IAnswer {
  from_student_id: number;
  to_student_id: number;
  value: string;
}

interface IFeedbackAnswer {
  id: number | null;
  student_id: number | undefined;
  full_name: string;
  contents: {
    [x: string]: any;
  };
}

interface ISelectedFeedbackDataHistory {
  answer_id: number;
  student_id: number;
  class_id: number;
  value?: string;
}

interface IFeedbackCommentInputProps {
  question_order_num: number;
  issue_no: number;
  current_student_id?: number;
  answer: ICurrentAnswer;
  classes: IClassWithStudents[];
  setAnswer: Dispatch<SetStateAction<ICurrentAnswer>>;
  feedbackAnswers: IFeedbackAnswer[];
  active_learning_answers: IActiveLearningAnswers[] | [];

  saveAnswers: () => void;
}

export const FeedbackCommentInput = (props: IFeedbackCommentInputProps) => {
  const [isShowModalConfirm, setIsShowModalConfirm] = useState(false);
  const [selectedClassIdForFeedback, setSelectedClassIdForFeedback] = useState(
    NaN,
  );
  const [
    selectedStudentIdForFeedback,
    setSelectedStudentIdForFeedback,
  ] = useState(NaN);
  const [
    selectedFeedbackDataHistories,
    setSelectedFeedbackDataHistory,
  ] = useState<ISelectedFeedbackDataHistory[]>([]);

  useEffect(() => {
    if (props.classes && props.classes[0]) {
      setSelectedClassIdForFeedback(props.classes[0].id);
    }
  }, [props.classes]);

  const isEditingValue = !!props.active_learning_answers.find(
    answer =>
      answer.issue_no === props.issue_no &&
      answer.question_no === props.question_order_num &&
      answer.contents?.to_student_id === props.answer.to_student_id &&
      answer.contents?.value,
  );

  const restoreSelectedDataForFeedback = () => {
    // キャンセルされたフィードバックの削除
    selectedFeedbackDataHistories.pop();
    // 元のフィードバックデータに戻す
    const lastFeedbackDataHistory =
      selectedFeedbackDataHistories[selectedFeedbackDataHistories.length - 1];
    setSelectedClassIdForFeedback(lastFeedbackDataHistory?.class_id || NaN);
    setSelectedStudentIdForFeedback(lastFeedbackDataHistory?.student_id || NaN);
    props.setAnswer({
      ...props.answer,
      id: lastFeedbackDataHistory?.answer_id || null,
      value: lastFeedbackDataHistory?.value || "",
      to_student_id: lastFeedbackDataHistory?.student_id,
    });
  };

  const setFeedbackDataWithHistory = (
    class_id: number,
    student_id: number,
    answer_value?: string,
  ) => {
    setSelectedStudentIdForFeedback(student_id);
    setSelectedClassIdForFeedback(class_id);
    const updateAttr = { id: NaN, to_student_id: student_id, value: "" };
    // 選択された生徒のフィードバックデータを取得
    if (!isNaN(student_id) && props.feedbackAnswers.length > 0) {
      const currentAnswer = props.feedbackAnswers.find(
        answer => answer.student_id === student_id,
      );
      updateAttr.id = currentAnswer?.id || NaN;
      updateAttr.value = currentAnswer?.contents?.value || "";
    }
    if (typeof answer_value === "string") {
      updateAttr.value = answer_value;
    }
    props.setAnswer({
      ...props.answer,
      ...updateAttr,
    });

    setSelectedFeedbackDataHistory([
      ...selectedFeedbackDataHistories,
      {
        answer_id: updateAttr.id,
        student_id,
        class_id,
        value: answer_value || updateAttr.value,
      },
    ]);
  };

  const isEditableAnswerForm =
    Number.isNaN(selectedStudentIdForFeedback) ||
    selectedStudentIdForFeedback === 0;

  const isChangedAnswerValue = useCallback(() => {
    if (props.answer.value.length === 0) return false;
    const currentAnswer = props.feedbackAnswers.find(
      answer => answer.student_id === selectedStudentIdForFeedback,
    );
    return currentAnswer?.contents?.value !== props.answer.value;
  }, [props.answer, props.feedbackAnswers, selectedStudentIdForFeedback]);

  const optionsForStudentSelector = props.classes
    .find(klass => klass.id === selectedClassIdForFeedback)
    ?.students.filter(student => student.id !== props.current_student_id)
    .sort((a, b) => a.last_name_kana.localeCompare(b.last_name_kana, "ja"))
    .map(student => ({
      value: student.id,
      label: `${student.last_name} ${student.first_name}`,
    }));

  return (
    <div className={styles.issue}>
      {isShowModalConfirm && (
        <ActiveLearningModalConfirm
          onClose={() => setIsShowModalConfirm(false)}
          title={
            "入力された内容はまだ保存されていません。保存せずに生徒を切り替えると、入力内容が失われます。よろしいですか？"
          }
          buttons={[
            {
              title: "いいえ",
              action: () => {
                restoreSelectedDataForFeedback();
                setIsShowModalConfirm(false);
              },
              type: "None",
            },
            {
              title: "はい",
              action: () => {
                setIsShowModalConfirm(false);
              },
              type: "Success",
            },
          ]}
        />
      )}
      <>
        <div className={styles.classSelectArea}>
          {props.classes.map(classItem => (
            <RadioButton
              key={classItem.id}
              value={classItem.id}
              selected={selectedClassIdForFeedback}
              handleChange={e => {
                setFeedbackDataWithHistory(Number(e.target.value), NaN);
                if (isChangedAnswerValue()) setIsShowModalConfirm(true);
              }}
            >
              {classItem.name}
            </RadioButton>
          ))}
        </div>
        <div className={styles.tree}>
          <Selector
            selectedValue={selectedStudentIdForFeedback}
            handleChange={e => {
              setFeedbackDataWithHistory(
                selectedClassIdForFeedback,
                Number(e.target.value),
              );
              if (isChangedAnswerValue()) setIsShowModalConfirm(true);
            }}
            options={optionsForStudentSelector}
          />
          <ul className={styles.branchList}>
            <li className={styles.branch}>
              <div className={styles.labelWrapper}>
                <span className={styles.label}>コメント</span>
              </div>
              <div className={styles.inputField}>
                <TextArea
                  value={props.answer.value}
                  handleChange={e =>
                    setFeedbackDataWithHistory(
                      selectedClassIdForFeedback,
                      selectedStudentIdForFeedback,
                      e.target.value,
                    )
                  }
                  disabled={isEditableAnswerForm}
                />
              </div>
            </li>
          </ul>
          <div className={styles.buttonWrapper}>
            <Button
              width={"158px"}
              height={"38px"}
              fontSize={"14px"}
              handleClick={() => {
                props.saveAnswers();
              }}
              disabled={isEditableAnswerForm}
            >
              {isEditingValue ? "コメントを上書き" : "送信する"}
            </Button>
          </div>
        </div>
      </>
    </div>
  );
};
export default FeedbackCommentInput;
